/**
 * The Alerts page's calls (docs/specs/cloud/alerts.md; 20261027000000_alerts.sql): the organisation's public push
 * key, the person's push subscriptions and alert choices, the organisation's recent alerts and acknowledging them,
 * and a test notice through the notify Edge Function. What a person may do is decided by RLS and the function.
 */
import type { SupabaseClient } from '@supabase/supabase-js';
import type { PushJson } from './push.ts';

const fail = (e: { message: string } | null): void => {
  if (e) throw new Error(e.message);
};

/** The Web Push public key; null while the keys are not set up. */
export async function loadPublicKey(sb: SupabaseClient): Promise<string | null> {
  const { data, error } = await sb.rpc('alert_push_public_key');
  fail(error);
  return typeof data === 'string' && data ? data : null;
}

export interface MyDevice {
  readonly id: string;
  readonly endpoint: string;
  /** The browser's automatic label ("Chrome on Android"), written each time it subscribes. */
  readonly device_label: string;
  /** The person's own name for it (R158), kept when the browser subscribes again; null for none. */
  readonly device_name: string | null;
  readonly created_at: string;
  readonly last_ok_at: string | null;
  readonly failures: number;
}

export async function loadMyDevices(sb: SupabaseClient, userId: string): Promise<MyDevice[]> {
  const { data, error } = await sb
    .from('push_subscription')
    .select('id, endpoint, device_label, device_name, created_at, last_ok_at, failures')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });
  fail(error);
  return (data ?? []) as MyDevice[];
}

/**
 * A phone signed in to Teslyn Mobile as this person (20261028160000_teslyn_mobile.sql; R158;
 * 20261104210100_mobile_device_identity.sql: one row per phone, per app).
 */
export interface MyPhone {
  readonly id: string;
  readonly platform: 'android' | 'ios';
  /** The phone's maker and model, rewritten each time the app opens ("Samsung SM-S938B"). */
  readonly label: string;
  /** The person's own name for it (R158), which the app never overwrites; null for none. */
  readonly device_name: string | null;
  readonly app_version: string;
  /** The app the row came from (`au.teslyn.mobile.beta`, `au.teslyn.mobile`); null from an app before 2026.10.21. */
  readonly app_id?: string | null;
  readonly created_at: string;
  readonly last_seen_at: string;
  /** null: the phone has not allowed notices, or turned alerts off (no push token). */
  readonly push_provider: 'fcm' | 'apns' | null;
  readonly push_expired_at: string | null;
  /** The person turned alerts off in Teslyn Mobile on it. */
  readonly push_off?: boolean;
}

/**
 * The person's signed-in phones, last opened first (never the push token itself). A phone replaced by its own
 * reinstall, signed out or removed is forgotten and not listed.
 */
export async function loadMyPhones(sb: SupabaseClient, userId: string): Promise<MyPhone[]> {
  const { data, error } = await sb
    .from('mobile_device')
    .select(
      'id, platform, label, device_name, app_version, app_id, created_at, last_seen_at, push_provider, push_expired_at, push_off',
    )
    .eq('user_id', userId)
    .is('forgotten_at', null)
    .order('last_seen_at', { ascending: false });
  fail(error);
  return (data ?? []) as MyPhone[];
}

/**
 * Remove one of the person's own phones from the list: it stops receiving their push alerts (the token is dropped,
 * audited). True when it was listed. The phone registers itself again the next time the app opens while still
 * signed in there.
 */
export async function removePhone(sb: SupabaseClient, id: string): Promise<boolean> {
  const { data, error } = await sb.rpc('mobile_device_remove', { p_device: id });
  fail(error);
  return data === true;
}

/** Name one of the person's own browsers or phones; an empty name goes back to the automatic label. */
export async function renameDevice(
  sb: SupabaseClient,
  kind: 'browser' | 'phone',
  id: string,
  name: string,
): Promise<string | null> {
  const { data, error } = await sb.rpc('alert_device_rename', { p_kind: kind, p_id: id, p_name: name });
  fail(error);
  return typeof data === 'string' && data ? data : null;
}

export async function saveDevice(
  sb: SupabaseClient,
  p: PushJson,
  label: string,
  origin: string,
): Promise<void> {
  const { error } = await sb.rpc('push_subscribe', {
    p_endpoint: p.endpoint,
    p_p256dh: p.p256dh,
    p_auth: p.auth,
    p_label: label,
    p_origin: origin,
  });
  fail(error);
}

export async function removeDevice(
  sb: SupabaseClient,
  by: { id?: string; endpoint?: string },
): Promise<void> {
  const q = sb.from('push_subscription').delete();
  const { error } = by.id ? await q.eq('id', by.id) : await q.eq('endpoint', by.endpoint ?? '');
  fail(error);
}

export interface Choice {
  readonly kind: string;
  readonly push: boolean | null;
  readonly email: boolean | null;
}

export async function loadChoices(sb: SupabaseClient, tenantId: string, userId: string): Promise<Choice[]> {
  const { data, error } = await sb
    .from('alert_preference')
    .select('kind, push, email')
    .eq('tenant_id', tenantId)
    .eq('user_id', userId);
  fail(error);
  return (data ?? []) as Choice[];
}

/** Keep a choice; both back to the default removes the row. */
export async function saveChoice(
  sb: SupabaseClient,
  tenantId: string,
  userId: string,
  c: Choice,
): Promise<void> {
  if (c.push === null && c.email === null) {
    const { error } = await sb
      .from('alert_preference')
      .delete()
      .eq('tenant_id', tenantId)
      .eq('user_id', userId)
      .eq('kind', c.kind);
    fail(error);
    return;
  }
  const { error } = await sb.from('alert_preference').upsert({
    tenant_id: tenantId,
    user_id: userId,
    kind: c.kind,
    push: c.push,
    email: c.email,
    updated_at: new Date().toISOString(),
  });
  fail(error);
}

export interface RecentAlert {
  readonly id: string;
  readonly kind: string;
  readonly title: string;
  readonly body: string;
  readonly occurred_at: string;
  readonly status: 'pending' | 'sent' | 'failed' | 'none' | 'snoozed';
  readonly recipients: number;
  readonly acknowledged_at: string | null;
  readonly acknowledged_by: string | null;
  readonly acknowledged_via: 'page' | 'push' | 'page_all' | null;
  /** The stored history (02-015, parity C3); absent before its migration. */
  readonly project_id?: string | null;
  readonly snoozed_until?: string | null;
  readonly snoozed_by?: string | null;
  readonly resolved_at?: string | null;
  readonly resolved_by?: string | null;
  readonly resolved_note?: string | null;
}

export const ALERT_COLUMNS =
  'id, kind, title, body, occurred_at, status, recipients, acknowledged_at, acknowledged_by, acknowledged_via, project_id, snoozed_until, snoozed_by, resolved_at, resolved_by, resolved_note';

/** The organisation's alerts of the last `days` days, newest first (`limit` at most; one project's with `projectId`). */
export async function loadRecent(
  sb: SupabaseClient,
  tenantId: string,
  days = 7,
  opts: { readonly projectId?: string; readonly limit?: number } = {},
): Promise<RecentAlert[]> {
  const since = new Date(Date.now() - days * 86_400_000).toISOString();
  let q = sb.from('alert_event').select(ALERT_COLUMNS).eq('tenant_id', tenantId).gte('occurred_at', since);
  if (opts.projectId) q = q.eq('project_id', opts.projectId);
  const { data, error } = await q.order('occurred_at', { ascending: false }).limit(opts.limit ?? 200);
  fail(error);
  return (data ?? []) as RecentAlert[];
}

/** Snooze an event for `minutes` (its kind, project and station or aircraft send nothing meanwhile); 0 ends it. */
export async function snooze(sb: SupabaseClient, eventId: string, minutes: number): Promise<void> {
  const { error } = await sb.rpc('alert_snooze', { p_event: eventId, p_minutes: minutes });
  fail(error);
}

/** Resolve an event (the problem is over), with a note; acknowledges it too. */
export async function resolve(sb: SupabaseClient, eventId: string, note: string): Promise<void> {
  const { error } = await sb.rpc('alert_resolve', { p_event: eventId, p_note: note });
  fail(error);
}

export async function reopen(sb: SupabaseClient, eventId: string): Promise<void> {
  const { error } = await sb.rpc('alert_reopen', { p_event: eventId });
  fail(error);
}

/** Staff register names by sign-in, for "acknowledged by". */
export async function loadNames(sb: SupabaseClient, tenantId: string): Promise<Map<string, string>> {
  const { data, error } = await sb.from('person').select('user_id, name').eq('tenant_id', tenantId);
  fail(error);
  const m = new Map<string, string>();
  for (const r of (data ?? []) as { user_id: string | null; name: string }[])
    if (r.user_id) m.set(r.user_id, r.name);
  return m;
}

/**
 * Remove an alert from the history (R159; tenant admins; 20261102150000): hidden from everyone, never deleted, so it
 * is not raised again; audited. True when removed, false when it already was.
 */
export async function removeAlert(sb: SupabaseClient, eventId: string): Promise<boolean> {
  const { data, error } = await sb.rpc('alert_remove', { p_event: eventId });
  fail(error);
  return data !== false;
}

export async function acknowledge(sb: SupabaseClient, eventId: string): Promise<void> {
  const { error } = await sb.rpc('alert_acknowledge', { p_event: eventId });
  fail(error);
}

/**
 * "Acknowledge all" (decision 835): acknowledge these events at once, as the caller. The page chooses them with the
 * domain's `noticesToAcknowledgeAll`; the database acknowledges only those that still wait and never SOS or lost
 * tracking (20261027160000_alert_acknowledge_all.sql). Returns how many it acknowledged.
 */
export async function acknowledgeAll(
  sb: SupabaseClient,
  tenantId: string,
  eventIds: readonly string[],
): Promise<number> {
  const { data, error } = await sb.rpc('alert_acknowledge_many', { p_tenant: tenantId, p_events: eventIds });
  fail(error);
  return (data as { acknowledged?: number } | null)?.acknowledged ?? 0;
}

/** A test notice of one kind to the caller's own devices or email: the result in words. */
export async function sendTestNotice(
  sb: SupabaseClient,
  tenantId: string,
  kind: string,
  channel: 'push' | 'email',
): Promise<string> {
  const { data, error } = await sb.functions.invoke<{ message?: string; error?: string }>('notify', {
    body: { action: 'test', tenant_id: tenantId, kind, channel },
  });
  if (error) {
    // FunctionsHttpError carries the JSON body with our message in `context` (as users/api.ts)
    let msg = 'The test could not be sent. Check your connection and try again.';
    const ctx = (error as { context?: unknown }).context;
    if (ctx instanceof Response) {
      try {
        const j = (await ctx.json()) as { error?: string };
        if (j.error) msg = j.error;
      } catch {
        /* keep the general words */
      }
    }
    throw new Error(msg);
  }
  return data?.message ?? 'Test sent.';
}
